import {
  Building2Icon,
  MailIcon,
  ShieldCheckIcon,
  Loader2Icon,
  CheckCircleIcon,
  AlertTriangleIcon,
  LogInIcon,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { invitationApi, type InvitationPublicInfo } from '../services/invitationApi'
import { useAuthStore } from '../store/auth'
import { getErrorMessage } from '../utils'

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated, checkAuth } = useAuthStore()

  const [invitation, setInvitation] = useState<InvitationPublicInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Form for new users
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  useEffect(() => {
    if (!token) return
    invitationApi
      .getInfo(token)
      .then(setInvitation)
      .catch(() => setError('Convite não encontrado ou invalido'))
      .finally(() => setLoading(false))
  }, [token])

  const handleAccept = async () => {
    if (!token) return

    if (!isAuthenticated) {
      if (!fullName || !password) {
        setError('Preencha nome e senha para criar sua conta')
        return
      }
      if (password !== confirmPassword) {
        setError('As senhas não conferem')
        return
      }
      if (password.length < 8) {
        setError('Senha deve ter pelo menos 8 caracteres')
        return
      }
    }

    setAccepting(true)
    setError(null)

    try {
      const body = isAuthenticated ? {} : { full_name: fullName, password }
      await invitationApi.accept(token, body)
    } catch (err: any) {
      // O handler global do backend embrulha o `detail` em `{ error: { message } }`;
      // `response.data.detail` não existe nessa forma e toda recusa (409 da sessão
      // divergente, 400 do convite expirado) virava o texto genérico.
      setError(getErrorMessage(err))
      setAccepting(false)
      return
    }

    // A membership nova só aparece no portal quando `/auth/me` é relido — sem isto o
    // seletor de organização seguia sem a org até o próximo login (#1031). O backend
    // já garantiu que a conta do convite É a da sessão (409 `sessao_diverge_do_convite`
    // caso contrário), então o que a releitura traz é desta mesma identidade. Fica
    // FORA do try: o aceite já aconteceu, e uma releitura que falhe não pode virar
    // "erro ao aceitar convite" (a pessoa tentaria de novo um convite já consumido).
    // `checkAuth` nunca rejeita: em falha ele aplica a política do store para qualquer
    // carga de página (logout), e a tela de sucesso passa a oferecer "Fazer Login" — a
    // org está lá, o login de novo a mostra. Aceito, e registrado aqui.
    if (isAuthenticated) {
      await checkAuth()
    }
    setSuccess(true)
    setAccepting(false)
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2Icon className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    )
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <CheckCircleIcon className="mx-auto mb-4 h-16 w-16 text-emerald-500" />
          <h1 className="mb-2 text-2xl font-bold text-gray-900">Convite Aceito!</h1>
          <p className="mb-6 text-gray-600">
            Voce agora faz parte de{' '}
            <span className="font-semibold">{invitation?.organization_name}</span>.
          </p>
          <button
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
            className="w-full rounded-lg bg-purple-600 py-3 font-medium text-white transition-colors hover:bg-purple-700"
          >
            {isAuthenticated ? 'Ir para o Dashboard' : 'Fazer Login'}
          </button>
        </div>
      </div>
    )
  }

  if (!invitation || invitation.is_expired || invitation.status !== 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <AlertTriangleIcon className="mx-auto mb-4 h-16 w-16 text-yellow-500" />
          <h1 className="mb-2 text-2xl font-bold text-gray-900">Convite Indisponivel</h1>
          <p className="mb-6 text-gray-600">
            {invitation?.is_expired
              ? 'Este convite expirou.'
              : error || `Este convite já foi ${invitation?.status || 'utilizado'}.`}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full rounded-lg bg-gray-600 py-3 font-medium text-white transition-colors hover:bg-gray-700"
          >
            Ir para Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg">
        <div className="mb-6 text-center">
          <Building2Icon className="mx-auto mb-3 h-12 w-12 text-purple-600" />
          <h1 className="text-2xl font-bold text-gray-900">Convite para Organizacao</h1>
          <p className="mt-1 text-gray-500">
            <span className="font-medium">{invitation.inviter_name}</span> convidou você para
            participar de:
          </p>
        </div>

        <div className="mb-6 space-y-3 rounded-xl bg-gray-50 p-4">
          <div className="flex items-center gap-3">
            <Building2Icon className="h-5 w-5 text-gray-400" />
            <div>
              <div className="text-sm text-gray-500">Organizacao</div>
              <div className="font-semibold text-gray-900">{invitation.organization_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
            <div>
              <div className="text-sm text-gray-500">Funcao</div>
              <div className="font-semibold text-gray-900">{invitation.role_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MailIcon className="h-5 w-5 text-gray-400" />
            <div>
              <div className="text-sm text-gray-500">Email</div>
              <div className="font-semibold text-gray-900">{invitation.email}</div>
            </div>
          </div>
        </div>

        {isAuthenticated && user ? (
          <div className="mb-4">
            <p className="mb-4 text-sm text-gray-600">
              Logado como <span className="font-medium">{user.email}</span>
            </p>
          </div>
        ) : invitation.user_exists ? (
          <div className="mb-6">
            <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <LogInIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Voce já tem uma conta</p>
                <p className="mt-1 text-sm text-blue-700">
                  Faca login com <span className="font-medium">{invitation.email}</span> para
                  aceitar o convite.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                sessionStorage.setItem('invite_redirect', `/invite/${token}`)
                window.location.href = '/login'
              }}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 py-3 font-medium text-white transition-colors hover:bg-purple-700"
            >
              <LogInIcon className="h-4 w-4" />
              Fazer Login
            </button>
          </div>
        ) : (
          <div className="mb-6 space-y-4">
            <p className="text-sm font-medium text-gray-600">
              Crie sua conta para aceitar o convite:
            </p>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Nome completo</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                placeholder="Seu nome"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Senha</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                placeholder="Minimo 8 caracteres"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Confirmar senha
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-500"
                placeholder="Repita a senha"
              />
            </div>
          </div>
        )}

        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {(!invitation.user_exists || isAuthenticated) && (
          <button
            onClick={handleAccept}
            disabled={accepting}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 py-3 font-medium text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
          >
            {accepting ? (
              <>
                <Loader2Icon className="h-4 w-4 animate-spin" />
                Aceitando...
              </>
            ) : (
              'Aceitar Convite'
            )}
          </button>
        )}
      </div>
    </div>
  )
}
